/**
 * As chamadas da tela de contratos (`TP-049`, T9).
 *
 * Tudo o que a tela sabe sobre `/contratos` e `/fontes` passa por aqui —
 * mesma disciplina de `features/falhas-de-email/api/falhasDeEmail.ts`.
 */

import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { requisitar, requisitarComStatus } from '../../../shared/api/clienteHttp';
import {
  ENTIDADE_PARA_API,
  TIPO_DE_FONTE_PARA_API,
  mapearContrato,
  mapearFonteDetalhe,
  mapearPendencia,
  mapearFonteResumo,
  mapearPaginaDeComparacao,
  mapearPaginaDeItens,
} from './mapeadores';
import type {
  CadastroDeContrato,
  CadastroDeContratoApi,
  ConferenciaDoItem,
  EstadoDaFonte,
  Contrato,
  ContratoApi,
  FonteDetalhe,
  FonteDetalheApi,
  FonteEnviadaApi,
  FonteResumo,
  FonteResumoApi,
  PaginaDeComparacao,
  PaginaDeComparacaoApi,
  PaginaDeItens,
  PaginaDeItensApi,
  PendenciaDaFonte,
  PendenciaDaFonteApi,
  ResultadoDoEnvio,
  TipoDeFonte,
} from './tipos';

/**
 * Estados que ainda vão mudar sem a pessoa fazer nada na tela (o leitor anda, ou alguém revisa em outra aba).
 * Enquanto houver fonte num deles, a lista e o detalhe se reconsultam sozinhos (`TP-067`): antes, o estado só
 * trocava com F5.
 */
const ESTADOS_QUE_MUDAM: ReadonlySet<EstadoDaFonte> = new Set<EstadoDaFonte>([
  'recebida',
  'texto_extraido',
  'mapeada',
  'regras_extraidas',
  'em_revisao',
]);

/** De quanto em quanto tempo a tela reconsulta uma fonte que ainda está mudando. */
export const INTERVALO_DA_LEITURA_MS = 10_000;

export function estadoAindaMuda(estado: EstadoDaFonte): boolean {
  return ESTADOS_QUE_MUDAM.has(estado);
}

export const chavesDeContratos = {
  raiz: ['contratos'] as const,
  lista: () => [...chavesDeContratos.raiz, 'lista'] as const,
  fontes: (contratoId: string) => [...chavesDeContratos.raiz, contratoId, 'fontes'] as const,
  fonte: (fonteId: string) => [...chavesDeContratos.raiz, 'fonte', fonteId] as const,
  pendencias: (fonteId: string) => [...chavesDeContratos.raiz, 'fonte', fonteId, 'pendencias'] as const,
  itens: (
    contratoId: string,
    competencia: string,
    busca: string,
    pagina: number,
    tamanho: number,
    conferencia: ConferenciaDoItem | '',
  ) => [...chavesDeContratos.raiz, contratoId, 'itens', competencia, busca, pagina, tamanho, conferencia] as const,
  comparacao: (a: string, b: string, competencia: string, busca: string, pagina: number, tamanho: number) =>
    [...chavesDeContratos.raiz, 'comparacao', a, b, competencia, busca, pagina, tamanho] as const,
};

export function opcoesDosContratos() {
  return queryOptions({
    queryKey: chavesDeContratos.lista(),
    queryFn: ({ signal }): Promise<Contrato[]> =>
      requisitar<ContratoApi[]>('/contratos', { sinal: signal }).then((itens) => itens.map(mapearContrato)),
  });
}

/**
 * `POST /contratos`: cadastra um contrato (operadora, entidade, UF). É a porta de entrada de
 * qualquer operadora — antes dele, só existiam os contratos do seed de desenvolvimento.
 */
export async function cadastrarContrato(cadastro: CadastroDeContrato): Promise<Contrato> {
  const corpo: CadastroDeContratoApi = {
    payerId: cadastro.operadoraId,
    entity: ENTIDADE_PARA_API[cadastro.entidade],
    uf: cadastro.uf,
    name: cadastro.nome.trim(),
  };
  return requisitar<ContratoApi>('/contratos', { method: 'POST', corpo }).then(mapearContrato);
}

export function opcoesDasFontes(contratoId: string) {
  return queryOptions({
    queryKey: chavesDeContratos.fontes(contratoId),
    queryFn: ({ signal }): Promise<FonteResumo[]> =>
      requisitar<FonteResumoApi[]>(`/contratos/${encodeURIComponent(contratoId)}/fontes`, { sinal: signal }).then(
        (itens) => itens.map(mapearFonteResumo),
      ),
    refetchInterval: (consulta) =>
      (consulta.state.data ?? []).some((fonte) => estadoAindaMuda(fonte.estado)) ? INTERVALO_DA_LEITURA_MS : false,
  });
}

export function opcoesDaFonte(fonteId: string) {
  return queryOptions({
    queryKey: chavesDeContratos.fonte(fonteId),
    queryFn: ({ signal }): Promise<FonteDetalhe> =>
      requisitar<FonteDetalheApi>(`/fontes/${encodeURIComponent(fonteId)}`, { sinal: signal }).then(mapearFonteDetalhe),
    // A situação ("em leitura", "parou na etapa X") muda sem o estado mudar: o detalhe também se reconsulta.
    refetchInterval: (consulta) =>
      consulta.state.data !== undefined && estadoAindaMuda(consulta.state.data.estado) ? INTERVALO_DA_LEITURA_MS : false,
  });
}

export function opcoesDasPendencias(fonteId: string) {
  return queryOptions({
    queryKey: chavesDeContratos.pendencias(fonteId),
    queryFn: ({ signal }): Promise<PendenciaDaFonte[]> =>
      requisitar<PendenciaDaFonteApi[]>(`/fontes/${encodeURIComponent(fonteId)}/pendencias`, { sinal: signal }).then(
        (lista) => lista.map(mapearPendencia),
      ),
  });
}

export function opcoesDosItens(
  contratoId: string,
  competencia: string,
  busca: string,
  pagina: number,
  tamanho: number,
  conferencia: ConferenciaDoItem | '' = '',
) {
  return queryOptions({
    queryKey: chavesDeContratos.itens(contratoId, competencia, busca, pagina, tamanho, conferencia),
    queryFn: ({ signal }): Promise<PaginaDeItens> => {
      const parametros = new URLSearchParams({ competencia, pagina: String(pagina), tamanho: String(tamanho) });
      if (busca.trim() !== '') parametros.set('busca', busca.trim());
      if (conferencia !== '') parametros.set('conferencia', conferencia);
      return requisitar<PaginaDeItensApi>(`/contratos/${encodeURIComponent(contratoId)}/itens?${parametros.toString()}`, {
        sinal: signal,
      }).then(mapearPaginaDeItens);
    },
    enabled: competencia.trim() !== '',
    // Trocar de página mantém a anterior na tela até a nova chegar (sem piscar o esqueleto).
    placeholderData: keepPreviousData,
  });
}

export function opcoesDaComparacao(
  a: string,
  b: string,
  competencia: string,
  busca: string,
  pagina: number,
  tamanho: number,
) {
  return queryOptions({
    queryKey: chavesDeContratos.comparacao(a, b, competencia, busca, pagina, tamanho),
    queryFn: ({ signal }): Promise<PaginaDeComparacao> => {
      const parametros = new URLSearchParams({ a, b, competencia, pagina: String(pagina), tamanho: String(tamanho) });
      if (busca.trim() !== '') parametros.set('busca', busca.trim());
      return requisitar<PaginaDeComparacaoApi>(`/contratos/comparacao?${parametros.toString()}`, {
        sinal: signal,
      }).then(mapearPaginaDeComparacao);
    },
    enabled: a !== '' && b !== '' && a !== b && competencia.trim() !== '',
    placeholderData: keepPreviousData,
  });
}

/**
 * `POST /contratos/{id}/fontes`, `multipart/form-data` (T4). `criada` vem do
 * STATUS — `200` é a fonte que já existia pelo (contrato, hash) (`D6`), `201`
 * é fonte nova. A assinatura `%PDF` e o tamanho são conferidos de novo aqui
 * pelo SERVIDOR; o pré-cheque do front (`EnvioDeFonte.tsx`) só evita a
 * ida à rede quando dá para saber sem perguntar à API.
 */
export async function enviarFonte(parametros: {
  contratoId: string;
  arquivo: File;
  tipo: TipoDeFonte;
  vigenciaInicio: string;
  vigenciaFim: string;
}): Promise<ResultadoDoEnvio> {
  const forma = new FormData();
  forma.append('file', parametros.arquivo);
  forma.append('type', TIPO_DE_FONTE_PARA_API[parametros.tipo]);
  forma.append('validFrom', parametros.vigenciaInicio);
  if (parametros.vigenciaFim.trim() !== '') forma.append('validTo', parametros.vigenciaFim.trim());

  const { dado, status } = await requisitarComStatus<FonteEnviadaApi>(
    `/contratos/${encodeURIComponent(parametros.contratoId)}/fontes`,
    { method: 'POST', corpo: forma },
  );

  return { fonteId: dado.id, criada: status === 201 };
}
